/**
 * 步骤实体
 * zxw
 * 创建时间 2026-02-01
 * 优化：增加用户滚动检测，避免自动滚动干扰用户操作
 */

import CNF from '../../incs/Config';
import { rootReactDom, Html2pdf } from '../../incs/FunHelper';
import { useState, useRef, useEffect, useCallback } from 'react';
import {
    Flex,
    theme,
    Button,
    Space,
    Avatar,
    Typography,
    Collapse,
    Divider,
    message,
    Select,
    Tag,
    Tooltip,
} from 'antd';
import {
    SyncOutlined,
    CopyOutlined,
    ExpandAltOutlined,
    CheckCircleFilled,
    ExportOutlined,
} from '@ant-design/icons';

import { XMarkdown } from '@ant-design/x-markdown';
import Latex from '@ant-design/x-markdown/plugins/Latex';   // 公式
import { CodeHighlighter } from '@ant-design/x';
import '@ant-design/x-markdown/themes/light.css';

// 思维链
import XinYuanThink from './XinYuanThink';

// 引入组件
import IOPutFullScreen from './IOPutFullScreen';
import {
    ImageSkeleton,
    IncompleteLink,
    TableSkeleton,
    HtmlSkeleton,
    IncompleteEmphasis,
    IncompleteInlineCode,
    WelcomeCard,
    Code,
} from '../../components/Incomplete';

// 图表
import ChartPiePercent from './reportTemplates/ChartPiePercent'; // 分类百分比（饼图）
import ChartLineUTACD from './reportTemplates/ChartLineUTACD'; // 每日用户活跃统计
import ChartLineUTECY from './reportTemplates/ChartLineUTECY'; // 年度入职人数趋势

import ChartLineDateCount from './reportTemplates/ChartLineDateCount'; // 日期数量走势组件（曲线图）
import ChartIntervalTypeCount from './reportTemplates/ChartIntervalTypeCount'; // 类型数量分布组件（柱状图）

import ChartILYearAmount from './reportTemplates/ChartILYearAmount';   // 年度金额趋势统计（柱状和曲线混合图）
import ChartIntervalMonthIOAmount from './reportTemplates/ChartIntervalMonthIOAmount'; // 月份收支统计（柱状图）

const { Text } = Typography;

const StepEntity = (props) => {
    const {
        token: {
            colorPrimary,
            isDarkMode,
            colorStepBgActive,
            colorTextBase,
            colorBgSolid,
            colorStepBg,
            fontSizeSM,
        },
    } = theme.useToken();
    const [messageApi, contextHolder] = message.useMessage();

    // 输入输出展开
    const [inputActiveKey, setInputActiveKey] = useState([]);
    const [outputActiveKey, setOutputActiveKey] = useState([]);

    // 输入输出格式
    const [inputFormat, setInputFormat] = useState('Json');
    const [outputFormat, setOutputFormat] = useState('Text');

    // 最终输出展开
    const [endOutPutActiveKey, setEndOutPutActiveKey] = useState(['1']);
    // 最终输出格式
    const [endOutPutFormat, setEndOutPutFormat] = useState('Markdown');

    /***************打印动画配置***************/
    const className = props.isDarkMode ? 'x-markdown-dark' : 'x-markdown-light';
    const [hasNextChunk, setHasNextChunk] = useState(true);
    const [index, setIndex] = useState(0);
    const timer = useRef(null);

    // 用户滚动控制
    const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
    const contentScrollRef = useRef(props.contentRef.current);

    // 实时计时，动态显示节点运行时间
    const [elapsed, setElapsed] = useState(0);

    // 当 stepIsRunning 变为 true 时启动计时，变 false 时清除
    useEffect(() => {
        if (!props.stepIsRunning) {
            setElapsed(0);
            return;
        }
        const start = performance.now();
        const interval = setInterval(() => {
            setElapsed(performance.now() - start);
        }, 100); // 100ms 刷新一次，兼顾性能与视觉流畅

        return () => clearInterval(interval);
    }, [props.stepIsRunning]);

    // 监听滚动容器，检测用户是否主动离开底部
    useEffect(() => {
        const container = props.contentRef.current;
        if (!container) return;
        contentScrollRef.current = container;

        const handleScroll = () => {
            if (!container) return;
            const { scrollTop, clientHeight, scrollHeight } = container;
            // 距离底部小于 20px 认为在底部，启用自动滚动；否则禁用
            const isAtBottom = scrollHeight - (scrollTop + clientHeight) <= 20;
            setAutoScrollEnabled(isAtBottom);
        };

        container.addEventListener('scroll', handleScroll);
        // 初始检查一次
        handleScroll();

        return () => {
            container.removeEventListener('scroll', handleScroll);
        };
    }, [props.contentRef.current]); // 依赖容器引用

    // [优化] 滚动节流：使用 requestAnimationFrame 确保每帧最多执行一次
    const rafIdRef = useRef(null);
    const scrollToBottomThrottled = useCallback(
        (el) => {
            if (!autoScrollEnabled) return;
            if (!el) return;
            // 如果已经有 pending 的滚动请求，取消旧的，保证只执行最后一次
            if (rafIdRef.current) {
                cancelAnimationFrame(rafIdRef.current);
            }
            rafIdRef.current = requestAnimationFrame(() => {
                if (el && index > 0) {
                    const { scrollHeight, clientHeight } = el;
                    if (scrollHeight > clientHeight) {
                        // [优化] 使用 auto 代替 smooth，避免平滑滚动带来的性能开销
                        el.scrollTo({ top: scrollHeight, behavior: 'auto' });
                    }
                }
                rafIdRef.current = null;
            });
        },
        [autoScrollEnabled, index],
    );

    useEffect(() => {
        // 切换模式时，滚动到底部（尊重用户意愿）
        if (autoScrollEnabled) {
            scrollToBottomThrottled(props.contentRef.current);
        }
    }, [isDarkMode, autoScrollEnabled, scrollToBottomThrottled]);

    useEffect(() => {
        if (props.node.key === 'End') {
            // 加上包裹标签的长度
            const length = props.node.output.result.length + (props.node.output.wrapLabel ? (props.node.output.wrapLabel.length * 2 + 1) : 0);
            if (index >= length) {
                setHasNextChunk(false);
                props.setRunStatus(false); // 输出结束
                setTimeout(() => {
                    // 输出结束后，如果自动滚动开启则滚动到底部
                    if (autoScrollEnabled) {
                        scrollToBottomThrottled(props.contentRef.current);
                    }
                }, 10);
                return;
            }
            timer.current = setTimeout(() => {
                setIndex(Math.min(index + 2, length));
            }, 20);
            return () => {
                if (timer.current) {
                    clearTimeout(timer.current);
                    timer.current = null;
                }
            };
        }
    }, [index]);

    useEffect(() => {
        if (props.node.key === 'End') {
            if (index > 0) {
                scrollToBottomThrottled(props.contentRef.current);
            }
        }
    }, [index, scrollToBottomThrottled]);
    /***************打印动画配置***************/

    useEffect(() => {
        if (props.node.outputMode) {
            setOutputFormat(props.node.outputMode);
        } else if (props.node.key === 'API' || props.node.key === 'Paramex') {
            // 兼容之前应用
            setOutputFormat('Json');
        }
    }, [props]);

    /**
     * 输入输出全屏展示
     * @param {*} title 
     * @param {*} format 
     * @param {*} content 
     */
    const openIOPutFullScreen = (title, format, content) => {
        rootReactDom && rootReactDom.render(
            <IOPutFullScreen
                open={true}
                isDarkMode={isDarkMode}
                colorPrimary={colorPrimary}
                appInfo={props.appInfo}
                title={title}
                format={format}
                content={content}
            />
        );
    }


    /**
     * 封闭标签
     * @param {*} openTag 
     * @returns 
     */
    const toClosingTag = (openTag) => {
        if (openTag.startsWith('<') && openTag.endsWith('>')) {
            return '</' + openTag.slice(1);
        }
        return openTag;
    }

    /**
     * 格式化输出
     * @param {*} output 
     * @returns 
     */
    const formatOutput = (output) => {
        if (output.wrapLabel) {
            return output.wrapLabel + output.result + toClosingTag(output.wrapLabel);
        }
        return output.result;
    };

    return <>
        {contextHolder}
        <Collapse
            size='small'
            ghost
            expandIconPlacement='end'
            style={{ border: 0 }}
            styles={{
                header: {
                    background: colorStepBg,
                    padding: 5,
                },
                body: {
                    padding: 5,
                }
            }}
            items={[{
                key: '1',
                label: <Space style={{ display: 'flex', alignItems: 'center' }}>
                    {<Avatar shape='square' size={22} style={{ background: props.node.theme, color: '#fff' }} icon={props.stepIsRunning ? <SyncOutlined spin /> : CNF.nodeConfig[props.node.key.toLowerCase()].icon} />}
                    <Text style={{ color: colorTextBase, fontSize: fontSizeSM, }}>{props.node.title ? props.node.title : props.node.nodeType}</Text>
                    {/* —— 核心改动：Tag 动态显示实时耗时或最终耗时 —— */}
                    {props.stepIsRunning ? (
                        <Tag
                            color="processing"
                            icon={<SyncOutlined spin />}
                            style={{
                                height: '16px',
                                lineHeight: '14px',
                                padding: '0 4px',
                                alignItems: 'center',
                                fontSize: '9px',
                            }}
                        >
                            <span>{Math.round(elapsed)}ms</span>
                        </Tag>
                    ) : (
                        <Tag
                            color="success"
                            icon={<CheckCircleFilled />}
                            style={{
                                height: '16px',
                                lineHeight: '14px',
                                padding: '0 4px',
                                alignItems: 'center',
                                fontSize: '9px',
                            }}
                        >
                            <span>{props.node.runTime}ms</span>
                        </Tag>
                    )}
                </Space>,
                children: <Space orientation='vertical' style={{ display: 'flex', }}>
                    <div>
                        {
                            // 输入
                            props.node.input ? <Collapse
                                collapsible='icon'
                                size='small'
                                ghost
                                style={{ border: `1px solid ${colorBgSolid}`, background: '#fff', }}
                                styles={{
                                    header: {
                                        background: colorStepBgActive,
                                        padding: 5,
                                        borderRadius: inputActiveKey.length > 0 ? '8px 8px 0 0' : '8px',
                                    },
                                    body: {
                                        background: '#fff',
                                        borderRadius: '0 0 8px 8px',
                                    },
                                }}
                                onChange={(key) => {
                                    setInputActiveKey(key);
                                }}
                                items={[{
                                    key: '1',
                                    label: <Flex style={{ color: colorTextBase, fontSize: fontSizeSM, display: 'flex', alignItems: 'center' }}>
                                        <div style={{ width: '60%' }}>输入</div>
                                        <div style={{ width: '40%', display: 'flex', justifyContent: 'flex-end' }}><Space size={0}>
                                            <Select
                                                size='small'
                                                variant='borderless'
                                                value={inputFormat}
                                                popupMatchSelectWidth={false}
                                                onChange={(value) => setInputFormat(value)}
                                                styles={{ root: { color: colorTextBase, fontSize: fontSizeSM, } }}
                                                options={CNF.displayFormats.map((item) => ({ value: item, label: item }))}
                                            />
                                            <Divider vertical />
                                            <Tooltip title='复制'>
                                                <Button
                                                    color='default'
                                                    variant='text'
                                                    size='small'
                                                    style={{ color: colorTextBase, fontSize: fontSizeSM, }}
                                                    onClick={() => {
                                                        // 获取不同格式的内容
                                                        let content = '';
                                                        if (inputFormat === 'Json') {
                                                            content = JSON.stringify(props.node.input, null, 2);
                                                        } else if (inputFormat === 'Text') {
                                                            content = JSON.stringify(props.node.input);
                                                        } else {
                                                            content = props.node.input;
                                                        }
                                                        if (navigator.clipboard && window.isSecureContext) {
                                                            navigator.clipboard.writeText(content).then(() => {
                                                                // 复制成功
                                                                messageApi.success('复制成功！', CNF.messageDuration);
                                                            }).catch((error) => {
                                                                // 复制失败
                                                                messageApi.error(error, CNF.messageDuration);
                                                            })
                                                        } else {
                                                            const input = document.createElement('textarea');
                                                            input.value = content;
                                                            document.body.appendChild(input);
                                                            input.select();
                                                            document.execCommand('copy');
                                                            document.body.removeChild(input);
                                                            messageApi.success('复制成功！', CNF.messageDuration);
                                                        }
                                                    }}
                                                ><CopyOutlined />
                                                </Button>
                                            </Tooltip>
                                            <Tooltip title='全屏'>
                                                <Button
                                                    color='default'
                                                    variant='text'
                                                    size='small'
                                                    style={{ color: colorTextBase, fontSize: fontSizeSM, }}
                                                    onClick={() => {
                                                        openIOPutFullScreen('输入', inputFormat, props.node.input)
                                                    }}
                                                ><ExpandAltOutlined />
                                                </Button>
                                            </Tooltip>
                                        </Space></div>
                                    </Flex>,
                                    children: <>
                                        {
                                            inputFormat === 'Json' ? <CodeHighlighter
                                                lang='json'
                                                styles={{
                                                    header: {
                                                        display: 'none',
                                                    },
                                                    code: {
                                                        border: 0,
                                                        background: '#fff'
                                                    }
                                                }}>{JSON.stringify(props.node.input, null, 2)}</CodeHighlighter>
                                                :
                                                inputFormat === 'Text' ? <Text style={{ color: colorTextBase, fontSize: fontSizeSM, }}>{JSON.stringify(props.node.input)}</Text>
                                                    : <XMarkdown
                                                        content={props.node.input}
                                                        streaming={{ enableAnimation: false, hasNextChunk, animationConfig: { fadeDuration: 200 } }} // 输入区通常不需要流式动画
                                                        className={'x-markdown-light'}
                                                        style={{
                                                            wordWrap: 'break-word',
                                                            overflowX: 'hidden',
                                                            overflowWrap: 'break-word',
                                                            padding: 8,
                                                        }}
                                                        components={{
                                                            code: Code,
                                                            'think': XinYuanThink,
                                                        }}
                                                        config={{
                                                            extensions: Latex(),
                                                        }}
                                                        paragraphTag='div'
                                                    />
                                        }
                                    </>
                                }]}
                            /> : ''
                        }
                    </div>
                    <div>
                        {
                            // 输出
                            props.node.output ? <Collapse
                                collapsible='icon'
                                size='small'
                                ghost
                                style={{ border: `1px solid ${colorBgSolid}`, background: '#fff', }}
                                styles={{
                                    header: {
                                        background: colorStepBgActive,
                                        padding: 5,
                                        borderRadius: outputActiveKey.length > 0 ? '8px 8px 0 0' : '8px',
                                    },
                                    body: {
                                        background: '#fff',
                                        borderRadius: '0 0 8px 8px',
                                    },
                                }}
                                onChange={(key) => {
                                    setOutputActiveKey(key);
                                }}
                                items={[{
                                    key: '2',
                                    label: <Flex style={{ color: colorTextBase, fontSize: fontSizeSM, display: 'flex', alignItems: 'center' }}>
                                        <div style={{ width: '60%' }}>输出</div>
                                        <div style={{ width: '40%', display: 'flex', justifyContent: 'flex-end' }}><Space size={0}>
                                            <Select
                                                size='small'
                                                variant='borderless'
                                                value={outputFormat}
                                                popupMatchSelectWidth={false}
                                                onChange={(value) => setOutputFormat(value)}
                                                styles={{ root: { color: colorTextBase, fontSize: fontSizeSM, } }}
                                                options={CNF.displayFormats.map((item) => ({ value: item, label: item }))}
                                            />
                                            <Divider vertical />
                                            <Tooltip title='复制'>
                                                <Button
                                                    color='default'
                                                    variant='text'
                                                    size='small'
                                                    style={{ color: colorTextBase, fontSize: fontSizeSM, }}
                                                    onClick={() => {
                                                        const outputContent = props.node.key === 'End' ? props.node.output.result : props.node.output;
                                                        // 获取不同格式的内容
                                                        let content = '';
                                                        if (outputFormat === 'Json') {
                                                            content = JSON.stringify(outputContent, null, 2);
                                                        } else if (outputFormat === 'Text') {
                                                            content = JSON.stringify(outputContent);
                                                        } else {
                                                            content = outputContent;
                                                        }
                                                        if (navigator.clipboard && window.isSecureContext) {
                                                            navigator.clipboard.writeText(content).then(() => {
                                                                // 复制成功
                                                                messageApi.success('复制成功！', CNF.messageDuration);
                                                            }).catch((error) => {
                                                                // 复制失败
                                                                messageApi.error(error, CNF.messageDuration);
                                                            })
                                                        } else {
                                                            const input = document.createElement('textarea');
                                                            input.value = content;
                                                            document.body.appendChild(input);
                                                            input.select();
                                                            document.execCommand('copy');
                                                            document.body.removeChild(input);
                                                            messageApi.success('复制成功！', CNF.messageDuration);
                                                        }
                                                    }}
                                                ><CopyOutlined />
                                                </Button>
                                            </Tooltip>
                                            <Tooltip title='全屏'>
                                                <Button
                                                    color='default'
                                                    variant='text'
                                                    size='small'
                                                    style={{ color: colorTextBase, fontSize: fontSizeSM, }}
                                                    onClick={() => {
                                                        openIOPutFullScreen('输出', outputFormat, props.node.output.result)
                                                    }}
                                                ><ExpandAltOutlined />
                                                </Button>
                                            </Tooltip>
                                        </Space></div>
                                    </Flex>,
                                    children: <>
                                        {
                                            outputFormat === 'Json' ? <CodeHighlighter
                                                lang='json'
                                                styles={{
                                                    header: {
                                                        display: 'none',
                                                    },
                                                    code: {
                                                        border: 0,
                                                        background: '#fff'
                                                    }
                                                }}>{props.node.key === 'End' ? JSON.stringify(props.node.output.result, null, 2) : JSON.stringify(props.node.output, null, 2)}</CodeHighlighter>
                                                :
                                                outputFormat === 'Text' ? <Text style={{ color: colorTextBase, fontSize: fontSizeSM, }}>{props.node.key === 'End' ? JSON.stringify(props.node.output.result) : JSON.stringify(props.node.output)}</Text>
                                                    : <XMarkdown
                                                        content={props.node.key === 'End' ? props.node.output.result : props.node.output}
                                                        streaming={{ enableAnimation: false, hasNextChunk, animationConfig: { fadeDuration: 200 } }}
                                                        className={'x-markdown-light'}
                                                        style={{
                                                            wordWrap: 'break-word',
                                                            overflowX: 'hidden',
                                                            overflowWrap: 'break-word',
                                                            padding: 8,
                                                        }}
                                                        components={{
                                                            code: Code,
                                                            'think': XinYuanThink,
                                                        }}
                                                        config={{
                                                            extensions: Latex(),
                                                        }}
                                                        paragraphTag='div'
                                                    />
                                        }
                                    </>
                                }]}
                            /> : ''
                        }
                    </div>
                </Space>
            }]}
        />
        {
            // 结束节点，工作流输出。
            props.node && props.node.key === 'End' ? <div style={{ background: props.isDarkMode ? '#000' : '#fff', paddingTop: 10, }}>
                <Collapse
                    size='small'
                    collapsible='icon'
                    ghost
                    expandIconPlacement='end'
                    style={{ border: `1px solid ${colorBgSolid}`, }}
                    styles={{
                        header: {
                            background: colorStepBg,
                            padding: 5,
                            borderRadius: endOutPutActiveKey.length ? '8px 8px 0 0' : '8px',
                        },
                        body: {
                            padding: 5,
                        }
                    }}
                    activeKey={endOutPutActiveKey}
                    onChange={(key) => {
                        setEndOutPutActiveKey(key);
                    }}
                    items={[{
                        key: '1',
                        label: <Space style={{ display: 'flex', alignItems: 'center' }}>
                            <Text style={{ color: colorTextBase, fontSize: fontSizeSM, }}>工作流最终输出：{props.node.title ? props.node.title : props.node.nodeType}节点</Text>
                            <Select
                                size='small'
                                variant='borderless'
                                value={endOutPutFormat}
                                popupMatchSelectWidth={false}
                                onChange={(value) => setEndOutPutFormat(value)}
                                styles={{ root: { color: colorTextBase, fontSize: fontSizeSM, } }}
                                options={CNF.displayFormats.map((item) => ({ value: item, label: item }))}
                            />
                            <Divider vertical />
                            <Tooltip title='复制'>
                                <Button
                                    color='default'
                                    variant='text'
                                    size='small'
                                    style={{ color: colorTextBase, fontSize: fontSizeSM, margin: 0 }}
                                    onClick={() => {
                                        // 获取不同格式的内容
                                        let content = formatOutput(props.node.output);
                                        if (endOutPutFormat === 'Json') {
                                            content = JSON.stringify(content, null, 2);
                                        } else if (endOutPutFormat === 'Text') {
                                            content = JSON.stringify(content);
                                        } else {
                                            content = content;
                                        }
                                        if (content.startsWith('"') && content.endsWith('"')) {
                                            // 去除前后的双引号
                                            content = content.slice(1, -1);
                                        }
                                        if (navigator.clipboard && window.isSecureContext) {
                                            navigator.clipboard.writeText(content).then(() => {
                                                // 复制成功
                                                messageApi.success('复制成功！', CNF.messageDuration);
                                            }).catch((error) => {
                                                // 复制失败
                                                messageApi.error(error, CNF.messageDuration);
                                            })
                                        } else {
                                            const input = document.createElement('textarea');
                                            input.value = content;
                                            document.body.appendChild(input);
                                            input.select();
                                            document.execCommand('copy');
                                            document.body.removeChild(input);
                                            messageApi.success('复制成功！', CNF.messageDuration);
                                        }
                                    }}
                                ><CopyOutlined />
                                </Button>
                            </Tooltip>
                            <Tooltip title='全屏'>
                                <Button
                                    color='default'
                                    variant='text'
                                    size='small'
                                    style={{ color: colorTextBase, fontSize: fontSizeSM, margin: 0 }}
                                    onClick={() => {
                                        openIOPutFullScreen('最终输出', endOutPutFormat, formatOutput(props.node.output));
                                    }}
                                ><ExpandAltOutlined />
                                </Button>
                            </Tooltip>
                            <Divider vertical style={{ padding: 0, margin: 0 }} />
                            <Tooltip title='导出'>
                                <Button
                                    color='default'
                                    variant='text'
                                    size='small'
                                    style={{ color: colorTextBase, fontSize: fontSizeSM, margin: 0 }}
                                    onClick={() => {
                                        // 导出
                                        Html2pdf('print-view-id', props.appInfo?.app_name);
                                    }}
                                ><ExportOutlined />
                                </Button>
                            </Tooltip>
                        </Space>,
                        children: <>
                            <div id='print-view-id'>
                                {
                                    endOutPutFormat === 'Json' ? <CodeHighlighter
                                        lang='json'
                                        styles={{
                                            header: {
                                                display: 'none',
                                            },
                                            code: {
                                                border: 0,
                                                background: '#fff'
                                            }
                                        }}>{props.node.key === 'End' ? JSON.stringify(formatOutput(props.node.output), null, 2) : JSON.stringify(props.node.output, null, 2)}</CodeHighlighter>
                                        :
                                        endOutPutFormat === 'Text' ? <Text style={{ color: colorTextBase, fontSize: fontSizeSM, }}>{props.node.key === 'End' ? JSON.stringify(formatOutput(props.node.output)) : JSON.stringify(props.node.output)}</Text>
                                            : <XMarkdown
                                                content={formatOutput(props.node.output).slice(0, index)}
                                                className={className}
                                                paragraphTag='div'
                                                openLinksInNewTab
                                                dompurifyConfig={{ ADD_ATTR: ['icon', 'description'] }}
                                                style={{
                                                    wordWrap: 'break-word',
                                                    overflowX: 'hidden',
                                                    overflowWrap: 'break-word',
                                                    padding: 8,
                                                }}
                                                components={{
                                                    code: Code,
                                                    welcome: WelcomeCard,
                                                    'think': XinYuanThink,
                                                    'incomplete-image': ImageSkeleton,
                                                    'incomplete-link': IncompleteLink,
                                                    'incomplete-table': TableSkeleton,
                                                    'incomplete-html': HtmlSkeleton,
                                                    'incomplete-emphasis': IncompleteEmphasis,
                                                    'incomplete-inline-code': IncompleteInlineCode,
                                                    'chart-pie-percent': ChartPiePercent,
                                                    'chart-line-utacd': ChartLineUTACD,
                                                    'chart-line-utecy': ChartLineUTECY,
                                                    'chart-line-date-count': ChartLineDateCount,
                                                    'chart-interval-type-count': ChartIntervalTypeCount,
                                                    'chart-il-year-amount': ChartILYearAmount,
                                                    'chart-interval-month-io-amount': ChartIntervalMonthIOAmount,
                                                }}
                                                config={{
                                                    extensions: Latex(),
                                                }}
                                                streaming={{ hasNextChunk }}
                                            />
                                }
                            </div>
                        </>
                    }]}
                /></div> : ''
        }
    </>
}

export default StepEntity;